import { create } from 'zustand';
import { AccessibilityInfo } from 'react-native';

interface AccessibilityState {
    highContrast: boolean;
    steadyStroke: boolean;
    lessonTextToSpeechOverride: boolean | null;
    reduceMotion: boolean;
    largeText: boolean;

    setHighContrast: (value: boolean) => void;
    setSteadyStroke: (value: boolean) => void;
    toggleHighContrast: () => void;
    toggleSteadyStroke: () => void;
    setLessonTextToSpeechOverride: (value: boolean) => void;
    clearLessonTextToSpeechOverride: () => void;
    setReduceMotion: (value: boolean) => void;
    setLargeText: (value: boolean) => void;
}

export const useAccessibilityStore = create<AccessibilityState>((set, get) => ({
    highContrast: false,
    steadyStroke: false,
    lessonTextToSpeechOverride: null,
    reduceMotion: false,
    largeText: false,

    setHighContrast: (value: boolean) => set({ highContrast: value }),
    setSteadyStroke: (value: boolean) => set({ steadyStroke: value }),
    toggleHighContrast: () => set({ highContrast: !get().highContrast }),
    toggleSteadyStroke: () => set({ steadyStroke: !get().steadyStroke }),
    setLessonTextToSpeechOverride: (value: boolean) => set({ lessonTextToSpeechOverride: value }),
    clearLessonTextToSpeechOverride: () => set({ lessonTextToSpeechOverride: null }),
    setReduceMotion: (value: boolean) => set({ reduceMotion: value }),
    setLargeText: (value: boolean) => set({ largeText: value }),
}));

/**
 * Initialize accessibility preferences from system settings.
 * Call once during app startup (e.g. in App.tsx useEffect).
 *
 * On web: reads `prefers-contrast: high` media query.
 * On native: currently defaults to false (AccessibilityInfo.isReduceTransparencyEnabled
 *            or similar could be used in future if RN adds contrast API).
 */
export function initializeAccessibilityFromSystem(): void {
    if (typeof globalThis !== 'undefined' && 'window' in globalThis) {
        const win = (globalThis as any).window;
        if (win && win.matchMedia) {
            const mq = win.matchMedia('(prefers-contrast: high)');
            useAccessibilityStore.getState().setHighContrast(mq.matches);
            // Listen for changes
            const listener = (e: { matches: boolean }) => {
                useAccessibilityStore.getState().setHighContrast(e.matches);
            };
            mq.addEventListener?.('change', listener);
        }
    }

    // On native platforms, check the system "Reduce Motion" setting and sync
    // it to the store. The API may not be available in all environments (e.g.
    // web, test runners, or older RN versions), so guard with try/catch.
    try {
        if (typeof AccessibilityInfo !== 'undefined' && AccessibilityInfo?.isReduceMotionEnabled) {
            AccessibilityInfo.isReduceMotionEnabled().then((enabled: boolean) => {
                useAccessibilityStore.getState().setReduceMotion(enabled);
            }).catch((err: unknown) => {
                console.warn('[useAccessibilityStore] Failed to read reduce-motion setting:', err);
            });
        }
    } catch (err) {
        console.warn('[useAccessibilityStore] AccessibilityInfo.isReduceMotionEnabled is not available:', err);
    }
}
